'use client';

import { useState } from 'react';

interface CopyLinkBoxProps {
  url: string;
  // 'amber'/'violet': light input + solid button (success page); 'dark': compact navy bar (/m, compose)
  variant?: 'amber' | 'violet' | 'dark';
  umamiEvent?: string;
}

const LIGHT_STYLES = {
  amber: { input: 'border-amber-300 focus:ring-amber-500', button: 'bg-amber-600 hover:bg-amber-700' },
  violet: { input: 'border-violet-300 focus:ring-violet-500', button: 'bg-violet-600 hover:bg-violet-700' },
};

export default function CopyLinkBox({ url, variant = 'amber', umamiEvent }: CopyLinkBoxProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    if (umamiEvent && typeof window.umami !== 'undefined') window.umami.track(umamiEvent);
  };

  if (variant === 'dark') {
    return (
      <div className="flex items-center gap-2 bg-[#07111e] border border-amber-500/20 rounded-xl px-4 py-3">
        <span className="text-amber-400 text-sm shrink-0">🔗</span>
        <span className="text-slate-400 text-xs truncate flex-1 font-mono">{url.replace(/^https?:\/\//, '')}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg transition-all bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
        >
          {copied ? '✓ Copied!' : 'Copy Link'}
        </button>
      </div>
    );
  }

  const styles = LIGHT_STYLES[variant];
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <input
        type="text"
        readOnly
        value={url}
        className={`flex-1 bg-white border rounded-lg px-4 py-3 text-sm text-slate-700 font-mono focus:outline-none focus:ring-2 ${styles.input}`}
      />
      <button
        onClick={handleCopy}
        className={`px-6 py-3 text-white text-sm font-bold rounded-lg transition-colors whitespace-nowrap shadow-sm ${copied ? 'bg-green-600 hover:bg-green-700' : styles.button}`}
      >
        {copied ? 'Copied! ✓' : 'Copy Link'}
      </button>
    </div>
  );
}
